import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, ShoppingCart, Plus, Minus, MessageCircle, ChevronLeft, ChevronRight, Check, X } from "lucide-react";
import SimilarProducts from "./SimilarProducts";
import { useAuth0 } from "@auth0/auth0-react"; // Import Auth0 hook

// Cart Success Modal Component
const CartSuccessModal = ({ isOpen, onClose, product, quantity, size }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Auto close after 3 seconds
      const timer = setTimeout(() => {
        handleClose();
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  
  const handleClose = () => {
    setIsAnimating(false);
    // Delay actual closing to allow exit animation to play
    setTimeout(() => {
      onClose();
    }, 300);
  };
  
  const goToCart = () => {
    handleClose();
    navigate("/cart");
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-300 ${
          isAnimating ? "opacity-40" : "opacity-0"
        }`}
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div 
        className={`bg-white rounded-sm shadow-xl max-w-md w-full relative transform transition-all duration-300 ${
          isAnimating ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        {/* Success Icon */}
        <div className="absolute -top-10 left-1/2 transform -translate-x-1/2">
          <div className={`bg-[#D4AF37] rounded-full p-3 border-4 border-white shadow-md ${
            isAnimating ? "animate-bounce" : ""
          }`}>
            <Check className="w-6 h-6 text-white" />
          </div>
        </div>
        
        {/* Close button */}
        <button 
          onClick={handleClose}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="p-6 pt-10">
          <h3 className="text-xl font-medium text-center text-[#333333] mb-4">
            Added to Cart!
          </h3>
          
          {product && (
            <div className="flex items-center gap-4 mb-4">
              {product.images && product.images.length > 0 ? (
                <img 
                  src={product.images[0]} 
                  alt={product.name} 
                  className="w-16 h-16 object-cover rounded-sm"
                />
              ) : (
                <div className="w-16 h-16 bg-gray-200 rounded-sm flex items-center justify-center">
                  <ShoppingCart className="w-6 h-6 text-gray-400" />
                </div>
              )}
              
              <div className="flex-1">
                <h4 className="font-medium text-sm text-[#333333]">{product.name}</h4>
                <p className="text-xs text-gray-500">
                  {size} · Qty: {quantity}
                </p>
                <p className="text-sm font-semibold text-[#D4AF37] mt-1">
                  ₹{(product.prices?.[size] || 0).toLocaleString()}
                </p>
              </div>
            </div>
          )}
          
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 bg-gray-100 text-[#333333] px-4 py-2 rounded-sm text-sm font-medium hover:bg-gray-200 transition-all duration-200"
            >
              Continue Shopping
            </button>
            <button
              onClick={goToCart}
              className="flex-1 bg-[#D4AF37] text-black px-4 py-2 rounded-sm text-sm font-medium hover:bg-[#C19B2E] transition-all duration-200 flex items-center justify-center"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              View Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#D4AF37]"></div>
  </div>
);

// Main ProductDetail Component
export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [selectedVolume, setSelectedVolume] = useState("100ml");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const AUTH_API = process.env.REACT_APP_AUTH0_API;
  
  // Add cart modal state
  const [cartModalOpen, setCartModalOpen] = useState(false);
  
  // Use Auth0 hooks instead of manual token handling
  const { 
    user: auth0User, 
    isLoading: auth0Loading, 
    isAuthenticated, 
    loginWithRedirect, 
    getAccessTokenSilently 
  } = useAuth0();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/product/${id}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setProduct(data.product);
        setSelectedVolume("100ml");
      } catch (error) {
        console.error("Failed to fetch product:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // Check if product is in user's wishlist (if authenticated)
  useEffect(() => {
    const checkWishlistStatus = async () => {
      if (!auth0Loading && isAuthenticated && auth0User && product) {
        try {
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: AUTH_API // Make sure this matches your backend API_IDENTIFIER
            }
          });
          
          const response = await fetch(`${API_BASE_URL}/user/wishlist/check/${product.id}`, {
            method: "GET",
            headers: {
              "Authorization": `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            setIsWishlisted(data.isWishlisted);
          }
        } catch (error) {
          console.error("Failed to check wishlist status:", error);
        }
      }
    };
    
    checkWishlistStatus();
  }, [auth0Loading, isAuthenticated, auth0User, product, getAccessTokenSilently]);

  const increaseQuantity = () => setQuantity(quantity + 1);
  const decreaseQuantity = () => quantity > 1 && setQuantity(quantity - 1);
  const handleVolumeChange = (volume) => setSelectedVolume(volume);
  
  const toggleWishlist = async () => {
    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      loginWithRedirect({
        appState: { 
          returnTo: `/product/${id}`,
          message: "Please login to add items to your wishlist" 
        }
      });
      return;
    }
    
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: AUTH_API
        }
      });
      
      const endpoint = isWishlisted 
        ? `${API_BASE_URL}/user/wishlist/remove/${product.id}`
        : `${API_BASE_URL}/user/wishlist/add/${product.id}`;
        
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      if (response.ok) {
        setIsWishlisted(!isWishlisted);
      }
    } catch (error) {
      console.error("Failed to update wishlist:", error);
    }
  };

  // Image Carousel Navigation
  const nextImage = () => {
    if (product?.images) {
      setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
    }
  };

  const prevImage = () => {
    if (product?.images) {
      setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  // Swipe Gesture Handling
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
    setTouchEnd(null);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isSwipe = Math.abs(distance) > 50; // Minimum swipe distance
    if (isSwipe) {
      if (distance > 0) {
        nextImage(); // Swipe left
      } else {
        prevImage(); // Swipe right
      }
    }
  };

  // Safely get the current price
  const getCurrentPrice = () => {
    if (!product?.prices) return 0;
    return Number(product.prices[selectedVolume]) || 0;
  };

  const currentPrice = getCurrentPrice();

  // Check if product has discount
  const hasDiscount = product?.offerType === "Discount" && 
                    product?.discountedPrices && 
                    product.discountedPrices[selectedVolume];

  // Get discounted price if available
  const getDiscountedPrice = () => {
    if (!hasDiscount) return currentPrice;
    return Number(product.discountedPrices[selectedVolume]) || currentPrice;
  };

  const discountedPrice = getDiscountedPrice();

  // Calculate discount percentage
  const discountPercentage = hasDiscount
    ? Math.round(((currentPrice - discountedPrice) / currentPrice) * 100)
    : 0;

  const addToCart = async () => {
    if (!isAuthenticated) {
      navigate("/profile");
      return;
    }
    
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: AUTH_API
        }
      });
      
      // Using the token for authentication
      const response = await fetch(`${API_BASE_URL}/cart/add/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: auth0User.email,
          product_id: product.id,
          size: selectedVolume,
          quantity: quantity
        })
      });
      
      if (response.ok) {
        //console.log(`Added ${quantity} of ${product?.name} (${selectedVolume}) to cart`);
        // Show success modal
        setCartModalOpen(true);
      } else {
        const errorData = await response.json();
        console.error("Failed to add to cart:", errorData.error);
        // Could add error toast notification here
      }
    } catch (error) {
      console.error("Failed to add to cart:", error);
    }
  };

  const buyNow = async () => {
    if (!isAuthenticated) {
      navigate("/profile");
      return;
    }
    
    // First add to cart, then navigate to cart page
    try {
      await addToCart();
      navigate("/cart");
    } catch (error) {
      console.error("Failed to process buy now:", error);
    }
  };

  const customizeBottle = () => {
    if (!product) return;
    const phoneNumber = "+917025959481";
    const message = `Hello, I would like to customize the bottle for:\n\n- Product: ${product.name}\n- Volume: ${selectedVolume}\n- Price: ₹${currentPrice}`;
    const encodedMessage = encodeURIComponent(message);
    window.location.href = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
  };

  if (auth0Loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#333333]">Checking authentication...</p>
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-center py-12 text-red-500">Error: {error}</div>;
  if (!product) return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4 md:px-8 text-center mt-16">
      <h1 className="text-3xl text-[#333333] mb-4">Product Not Found</h1>
      <Link
        to="/shop"
        className="inline-flex items-center bg-[#D4AF37] text-black px-6 py-2 rounded-sm text-sm font-medium hover:bg-[#C19B2E] transition-all duration-200"
      >
        Back to Shopping
      </Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4 md:px-8">
      {/* Conditional Authentication Suggestion Banner */}
      {!isAuthenticated && (
        <div className="text-center py-3 px-4 bg-amber-50 border-b border-amber-200 mb-6 rounded-sm">
          <p className="text-sm text-amber-800">
            Sign in to add to wishlist, save items to your cart, and get personalized recommendations!{" "}
            <Link to="/profile" className="font-semibold underline hover:text-amber-900">Log In</Link>
            {" "}or{" "}
            <Link to="/profile" className="font-semibold underline hover:text-amber-900">Sign Up</Link>
          </p>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl md:text-4xl text-[#333333] text-center mb-8 relative">
          {product.name}
          <span className="block w-24 h-1 bg-[#D4AF37] mx-auto mt-2"></span>
        </h1>

        <div className="flex flex-col md:flex-row gap-8 mb-12">
          <div className="md:w-1/2">
            <div className="relative w-full h-full rounded-sm overflow-hidden">
              {/* Image Carousel */}
              <div
                className="relative w-full max-h-full"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {product.images && product.images.length > 0 ? (
                  <img
                    src={product.images[currentImageIndex]}
                    alt={`${product.name} - Image ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-500">No image available</span>
                  </div>
                )}
                {/* Navigation Arrows */}
                {product.images && product.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-all duration-200"
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-all duration-200"
                      aria-label="Next image"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
              {/* Navigation Dots */}
              {product.images && product.images.length > 1 && (
                <div className="flex justify-center gap-2 mt-2">
                  {product.images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToImage(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-200 ${
                        currentImageIndex === index ? "bg-[#D4AF37] scale-125" : "bg-gray-400"
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="md:w-1/2">
            <h2 className="text-xl text-[#333333] mb-2">{product.name}</h2>
            <p className="text-sm text-gray-600 mb-2">{product.category}</p>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-[#D4AF37]">
                  ₹{discountedPrice.toLocaleString()}
                </span>
                {hasDiscount && (
                  <div className="flex items-center gap-2">
                    <span className="text-lg text-gray-500 line-through">
                      ₹{currentPrice.toLocaleString()}
                    </span>
                    <span className="text-sm bg-red-100 text-red-800 px-1.5 py-0.5 rounded">
                      {discountPercentage}% OFF
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">{product.description}</p>
            
            <div className="space-y-2 mb-6">
              <p className="text-sm text-gray-600">
                <strong>Fragrance Notes:</strong> {product.fragranceNotes.join(", ")}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Volume:</strong> {selectedVolume}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Availability:</strong>{" "}
                <span className={product.stockAvailability === "In stock" ? "text-green-600" : "text-red-600"}>
                  {product.stockAvailability}
                </span>
              </p>
              
              <div className="text-sm text-gray-600">
                <strong>Select Volume:</strong>
                <div className="flex gap-2 mt-1">
                  {product.prices && Object.keys(product.prices).map((volume) => (
                    <button
                      key={volume}
                      onClick={() => handleVolumeChange(volume)}
                      className={`px-3 py-1 rounded-sm border transition-all duration-200 ${
                        selectedVolume === volume
                          ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                          : "bg-white text-gray-600 border-gray-300 hover:bg-[#D4AF37] hover:text-black hover:border-[#D4AF37]"
                      }`}
                    >
                      {volume}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex flex-col gap-4 mb-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={decreaseQuantity}
                  className="p-1 rounded-sm border border-gray-300 hover:bg-gray-100"
                >
                  <Minus className="w-4 h-4 text-gray-600" />
                </button>
                <span className="text-sm text-gray-600">{quantity}</span>
                <button
                  onClick={increaseQuantity}
                  className="p-1 rounded-sm border border-gray-300 hover:bg-gray-100"
                >
                  <Plus className="w-4 h-4 text-gray-600" />
                </button>
                <button
                  onClick={toggleWishlist}
                  className={`ml-4 p-2 rounded-sm border border-gray-300 hover:bg-gray-100 ${
                    isWishlisted ? "text-red-500" : "text-gray-600"
                  }`}
                >
                  <Heart className="w-5 h-5" />
                </button>
              </div>
              
              <div className="flex flex-col gap-3">
                <div className="flex gap-3">
                  <button
                    onClick={addToCart}
                    className="flex-1 bg-[#D4AF37] text-black px-4 py-2 rounded-sm text-sm font-medium hover:bg-[#C19B2E] transition-all duration-200 flex items-center justify-center"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Add to Cart
                  </button>
                  <button
                    onClick={buyNow}
                    className="flex-1 bg-[#333333] text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-[#4a4a4a] transition-all duration-200 flex items-center justify-center"
                  >
                    Buy Now
                  </button>
                </div>
                <button
                  onClick={customizeBottle}
                  className="w-full bg-gradient-to-r from-[#D4AF37] to-[#C19B2E] text-black px-4 py-2 rounded-sm text-sm font-medium hover:from-[#C19B2E] hover:to-[#B08927] transition-all duration-200 flex items-center justify-center"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Customize Bottle
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-12">
          <h2 className="text-2xl text-[#333333] mb-4">Product Description</h2>
          <p className="text-sm text-gray-600 leading-relaxed">
            {product.description}
          </p>
        </div>

        <SimilarProducts currentProductId={product.id} category={product.category} />
      </div>
      
      {/* Cart Success Modal */}
      <CartSuccessModal
        isOpen={cartModalOpen}
        onClose={() => setCartModalOpen(false)}
        product={product}
        quantity={quantity}
        size={selectedVolume}
      />
    </div>
  );
}
